/**
 * Feedback Splash Data — Particle definitions and display config builder.
 *
 * Extracted from FeedbackSplash.tsx (667→~490 lines) during CL-1 s64.
 * Pure data + pure function — no React dependency.
 *
 * App-Feel §16.E (v2.13): confetti palettes can now be derived from the
 * active module's hex color so each world's celebration speaks the world's
 * language. The legacy generic palettes are kept as fallback for any caller
 * that doesn't have a module context (e.g. cross-module sessions).
 */

import type { DjayEmotion, DjayTier, DjayVariant } from '../../../mascot'

// ── XP Counter Animation Config (R8-MN2) ────────────────────

/** Number of discrete steps in the XP count-up animation. */
export const XP_COUNTER_STEPS = 15

/** Total duration (ms) of the XP count-up animation. */
export const XP_COUNTER_DURATION_MS = 500

// ── Particle Types ───────────────────────────────────────────

export interface ConfettiParticle {
  id: number
  /** Hex color (e.g. '#F97316') — applied via inline style, not Tailwind. */
  color: string
  size: string
  startX: number
  startY: number
  endX: number
  endY: number
  rotation: number
  delay: number
  duration: number
}

export interface WarmParticle {
  id: number
  color: string
  size: string
  startX: number
  startY: number
  endY: number
  drift: number
  delay: number
  duration: number
}

// ── Color Utilities ──────────────────────────────────────────

/**
 * Lighten a 6-digit hex color toward white by `amount` (0..1).
 * Used to derive softer companions of the module hue for confetti accents.
 */
function lightenHex(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lr = Math.round(r + (255 - r) * amount)
  const lg = Math.round(g + (255 - g) * amount)
  const lb = Math.round(b + (255 - b) * amount)
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(lr)}${toHex(lg)}${toHex(lb)}`
}

// ── Confetti Palettes ────────────────────────────────────────

/** Universal warm accents — gold + cream — that pair with any module hue. */
const UNIVERSAL_ACCENTS = ['#FBBF24', '#FEF3C7'] // amber-400, amber-100

/** Generic fallback palette (legacy v2.12 colors) when no module context. */
const FALLBACK_PALETTE = [
  '#FBBF24', // amber-400
  '#FB923C', // orange-400
  '#FACC15', // yellow-400
  '#FDA4AF', // rose-300
  '#A5B4FC', // indigo-300
  '#6EE7B7', // emerald-300
]

const FALLBACK_EXTRA_PALETTE = [
  '#C084FC', // purple-400
  '#F0ABFC', // fuchsia-300
  '#F472B6', // pink-400
  '#FCD34D', // amber-300
  '#FDE047', // yellow-300
  '#818CF8', // indigo-400
]

/**
 * Build a 6-color confetti palette anchored on the module's hex color.
 *
 * Composition: module hex + 2 progressively lightened variants + 1 even
 * lighter near-cream + 2 universal warm accents (amber + cream). The result
 * is visually module-flavored but never monochrome — confetti must still
 * "feel like a celebration", not a paint-roller of one color.
 */
export function buildModuleConfettiPalette(moduleColor?: string): string[] {
  if (!moduleColor || !/^#[0-9a-fA-F]{6}$/.test(moduleColor)) {
    return FALLBACK_PALETTE
  }
  return [
    moduleColor,
    lightenHex(moduleColor, 0.25),
    lightenHex(moduleColor, 0.5),
    lightenHex(moduleColor, 0.75),
    UNIVERSAL_ACCENTS[0],
    UNIVERSAL_ACCENTS[1],
  ]
}

/**
 * Build a complementary "extra confetti" palette — used only on level-up /
 * tier-evolution / skin-unlock splashes for added pop. Mirrors the main
 * palette but offsets the lightening curve so the burst feels layered.
 */
export function buildModuleExtraConfettiPalette(
  moduleColor?: string,
): string[] {
  if (!moduleColor || !/^#[0-9a-fA-F]{6}$/.test(moduleColor)) {
    return FALLBACK_EXTRA_PALETTE
  }
  return [
    lightenHex(moduleColor, 0.1),
    lightenHex(moduleColor, 0.35),
    lightenHex(moduleColor, 0.6),
    '#FCD34D', // amber-300
    '#FDE047', // yellow-300
    '#F472B6', // pink-400 — universal pop
  ]
}

// ── Warm Particles (incorrect splash) ────────────────────────
// These are intentionally NOT themed per module: a wrong answer is a
// neutral, encouraging moment — warm gold/amber works across every world
// and avoids any visual link between "you got it wrong" and the module's
// identity color (which could feel like the world is judging the learner).

const WARM_COLORS = ['#FED7AA', '#FDE68A', '#FEF08A'] // orange-200, amber-200, yellow-200

// ── Particle Generators ──────────────────────────────────────

export function generateConfettiParticles(
  count: number,
  palette: string[],
  idOffset = 0,
): ConfettiParticle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i + idOffset,
    color: palette[i % palette.length],
    size: Math.random() > 0.5 ? 'w-2.5 h-2.5' : 'w-2 h-2',
    startX: 50 + (Math.random() - 0.5) * (idOffset ? 40 : 30),
    startY: idOffset ? 40 : 35,
    endX: Math.random() * 100,
    endY: 90 + Math.random() * (idOffset ? 20 : 15),
    rotation: Math.random() * (idOffset ? 720 : 540) - (idOffset ? 360 : 270),
    delay: Math.random() * (idOffset ? 0.4 : 0.25),
    duration: (idOffset ? 1.5 : 1.2) + Math.random() * (idOffset ? 0.5 : 0.4),
  }))
}

/**
 * Build the main + extra confetti arrays for a splash, themed on moduleColor.
 * Wrapped in a single call so FeedbackSplash can `useMemo` cleanly.
 */
export function buildConfettiSet(moduleColor?: string): {
  main: ConfettiParticle[]
  extra: ConfettiParticle[]
} {
  const mainPalette = buildModuleConfettiPalette(moduleColor)
  const extraPalette = buildModuleExtraConfettiPalette(moduleColor)
  return {
    main: generateConfettiParticles(12, mainPalette),
    extra: generateConfettiParticles(12, extraPalette, 100),
  }
}

export const WARM_PARTICLES: WarmParticle[] = Array.from(
  { length: 6 },
  (_, i) => ({
    id: i,
    color: WARM_COLORS[i % 3],
    size: 'w-2 h-2',
    startX: 15 + Math.random() * 70,
    startY: 100 + Math.random() * 15,
    endY: -15,
    drift: (Math.random() - 0.5) * 40,
    delay: i * 0.4,
    duration: 5 + Math.random() * 2,
  }),
)

// ── Display Config ───────────────────────────────────────────

export type SplashPriority =
  | 'levelUp'
  | 'tierEvolution'
  | 'skinUnlock'
  | 'dailyGoal'
  | 'streak'
  | 'correct'
  | 'incorrect'

export interface SplashDisplayConfig {
  mainMessage: string
  subMessage: string
  emotion: DjayEmotion
  duration: number
  gradient: string
  showExtraConfetti: boolean
}

interface SplashInput {
  isCorrect: boolean
  streak: number
  levelUp?: { level: number; xp: number; title: string } | null
  tierEvolution?: { from: DjayTier; to: DjayTier } | null
  skinUnlock?: { skin: DjayVariant; description: string } | null
  dailyGoalReached?: boolean
  streakMilestone?: { count: number; message: string } | null
  splashMsg: { main: string; sub: string }
}

const WARM_GRADIENT = '#FEF3C7'
const EVOLUTION_GRADIENT = '#F3E8FF'
const INCORRECT_GRADIENT = '#FFF7ED'

export function resolveSplashPriority(input: SplashInput): SplashPriority {
  const {
    isCorrect,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
  } = input
  if (levelUp && isCorrect) return 'levelUp'
  if (tierEvolution && isCorrect) return 'tierEvolution'
  if (skinUnlock && isCorrect) return 'skinUnlock'
  if (dailyGoalReached && isCorrect) return 'dailyGoal'
  if (streakMilestone && isCorrect) return 'streak'
  return isCorrect ? 'correct' : 'incorrect'
}

export function buildSplashDisplayConfig(
  input: SplashInput,
): SplashDisplayConfig {
  const priority = resolveSplashPriority(input)
  const { levelUp, skinUnlock, streakMilestone, splashMsg, streak } = input

  switch (priority) {
    case 'levelUp':
      return {
        mainMessage: `Level Up! Level ${levelUp?.level}! 🎉`,
        subMessage: levelUp?.title ?? '',
        emotion: 'proud',
        duration: 3000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: true,
      }
    case 'tierEvolution':
      return {
        mainMessage: 'DJAY EVOLUEERT! ✨',
        subMessage: `Level ${levelUp?.level || ''} — ${levelUp?.title || ''} 🌟`,
        emotion: 'proud',
        duration: 3500,
        gradient: EVOLUTION_GRADIENT,
        showExtraConfetti: true,
      }
    case 'skinUnlock':
      return {
        mainMessage: 'NIEUW ONTGRENDELD! 🎨',
        subMessage: skinUnlock?.description ?? '',
        emotion: 'celebrating',
        duration: 3000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: true,
      }
    case 'dailyGoal':
      return {
        mainMessage: 'Dagelijks doel gehaald! 🎯',
        subMessage: '+50 XP bonus!',
        emotion: 'celebrating',
        duration: 2500,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'streak':
      return {
        mainMessage: streakMilestone?.message ?? '',
        subMessage: splashMsg.main,
        emotion: 'excited',
        duration: 2500,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'correct':
      return {
        mainMessage: splashMsg.main,
        subMessage: splashMsg.sub,
        emotion: streak >= 5 ? 'excited' : 'celebrating',
        duration: 2000,
        gradient: WARM_GRADIENT,
        showExtraConfetti: false,
      }
    case 'incorrect':
    default:
      return {
        mainMessage: splashMsg.main,
        subMessage: splashMsg.sub,
        emotion: 'encouraging',
        duration: 2500,
        gradient: INCORRECT_GRADIENT,
        showExtraConfetti: false,
      }
  }
}
